import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { 
  Code, Layers, Database, FileCode2, Terminal, Globe,
  PenTool, ServerIcon, BoxIcon, MonitorSmartphone, Brain,
  Users, Lightbulb, HeartHandshake, MessagesSquare, Sparkles,
  GraduationCap, Calendar, Building2, ChevronRight, ArrowRight,
  Orbit, Zap, Coffee, Smile, Rocket, Star, Map, User
} from "lucide-react";

import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { staggerChildren: 0.08 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.5 }
  }
};

const floatingVariants = {
  animate: {
    y: [0, -10, 0],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: "easeInOut"
    }
  }
};

// Skill data
const hardSkills = [
  { name: "React", icon: <Code className="h-5 w-5" />, color: "bg-blue-500", level: 95 },
  { name: "Node.js", icon: <Terminal className="h-5 w-5" />, color: "bg-green-600", level: 90 },
  { name: "HTML/CSS", icon: <FileCode2 className="h-5 w-5" />, color: "bg-orange-500", level: 98 },
  { name: "JavaScript", icon: <Globe className="h-5 w-5" />, color: "bg-yellow-500", level: 92 },
  { name: ".NET 8", icon: <Layers className="h-5 w-5" />, color: "bg-purple-600", level: 85 },
  { name: "Blazor", icon: <ServerIcon className="h-5 w-5" />, color: "bg-violet-600", level: 80 },
  { name: "Docker", icon: <BoxIcon className="h-5 w-5" />, color: "bg-blue-600", level: 75 },
  { name: "Active Directory", icon: <Database className="h-5 w-5" />, color: "bg-zinc-600", level: 85 },
  { name: "React Native", icon: <MonitorSmartphone className="h-5 w-5" />, color: "bg-blue-500", level: 88 },
  { name: "Figma", icon: <PenTool className="h-5 w-5" />, color: "bg-pink-600", level: 90 },
  { name: "Microsoft Azure", icon: <ServerIcon className="h-5 w-5" />, color: "bg-blue-700", level: 82 },
  { name: "TailwindCSS", icon: <FileCode2 className="h-5 w-5" />, color: "bg-sky-500", level: 95 },
];

const softSkills = [
  { name: "Problem Solving", icon: <Brain className="h-5 w-5" />, color: "bg-amber-500" },
  { name: "Teamwork", icon: <Users className="h-5 w-5" />, color: "bg-blue-500" },
  { name: "Creativity", icon: <Lightbulb className="h-5 w-5" />, color: "bg-yellow-500" },
  { name: "Leadership", icon: <Sparkles className="h-5 w-5" />, color: "bg-purple-500" },
  { name: "Communication", icon: <MessagesSquare className="h-5 w-5" />, color: "bg-green-500" },
  { name: "Adaptability", icon: <HeartHandshake className="h-5 w-5" />, color: "bg-red-500" },
];

// Education data
const education = [
  {
    degree: "Postgraduate IT Management",
    school: "HoGent",
    period: "2024-2025",
    description: "Advanced studies in IT project management, leadership, and strategic technology planning.",
    icon: <Star className="w-4 h-4" />
  },
  {
    degree: "Bachelor in Applied Computer Science",
    school: "HoGent",
    period: "2021-2024",
    description: "Comprehensive education in software development, system architecture, and IT infrastructure.",
    icon: <Zap className="w-4 h-4" />
  },
  {
    degree: "Erasmus Semester",
    school: "NTNU, Norway",
    period: "2023",
    description: "International exchange program focused on advanced programming techniques and AI applications.",
    icon: <Map className="w-4 h-4" />
  },
  {
    degree: "Information Systems Management",
    school: "GO! Atheneum Oudenaarde",
    period: "2019-2021",
    description: "Foundation in IT systems, network management, and business applications.",
    icon: <ServerIcon className="w-4 h-4" />
  },
  {
    degree: "Science Mathematics",
    school: "Bernardus college Oudenaarde",
    period: "2015-2019",
    description: "Strong foundation in analytical thinking, problem-solving, and mathematical principles.",
    icon: <Orbit className="w-4 h-4" />
  },
];

const SkillMasteryGrid = ({ skills }) => {
  const [activeCategory, setActiveCategory] = useState(null);
  
  // Group skills by mastery level
  const masterLevels = {
    expert: skills.filter(s => s.level >= 90),
    advanced: skills.filter(s => s.level >= 80 && s.level < 90),
    intermediate: skills.filter(s => s.level >= 70 && s.level < 80)
  };
  
  return (
    <div className="w-full">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
        {/* Expert level */}
        <motion.div 
          className="relative"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          onMouseEnter={() => setActiveCategory('expert')}
          onMouseLeave={() => setActiveCategory(null)}
        >
          <div className={`h-full rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6 shadow-sm transition-all duration-300 ${activeCategory === 'expert' ? 'shadow-xl ring-2 ring-blue-500/30 dark:ring-blue-400/20' : 'hover:shadow-md'}`}>
            <div className="mb-4 flex items-center">
              <div className="mr-3 h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 dark:from-blue-600 dark:to-blue-800 text-white flex items-center justify-center">
                <Star className="h-5 w-5" />
              </div>
              <h4 className="text-xl font-bold text-zinc-900 dark:text-white">Expert</h4>
            </div>
            
            <div className="space-y-3 mt-5">
              {masterLevels.expert.map(skill => (
                <div key={skill.name} className="flex items-center gap-2">
                  <div className={`${skill.color} bg-opacity-90 dark:bg-opacity-80 h-8 w-8 rounded-md flex items-center justify-center text-white`}>
                    {skill.icon}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{skill.name}</span>
                      <span className="text-xs text-zinc-500 dark:text-zinc-400">{skill.level}%</span>
                    </div>
                    <div className="h-1.5 w-full bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
                      <motion.div
                        className={`h-full ${skill.color} rounded-full`}
                        initial={{ width: 0 }}
                        whileInView={{ width: `${skill.level}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: 0.3 }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
        
        {/* Advanced level */}
        <motion.div 
          className="relative"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          onMouseEnter={() => setActiveCategory('advanced')}
          onMouseLeave={() => setActiveCategory(null)}
        >
          <div className={`h-full rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6 shadow-sm transition-all duration-300 ${activeCategory === 'advanced' ? 'shadow-xl ring-2 ring-purple-500/30 dark:ring-purple-400/20' : 'hover:shadow-md'}`}>
            <div className="mb-4 flex items-center">
              <div className="mr-3 h-10 w-10 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 dark:from-purple-600 dark:to-purple-800 text-white flex items-center justify-center">
                <Zap className="h-5 w-5" />
              </div>
              <h4 className="text-xl font-bold text-zinc-900 dark:text-white">Advanced</h4>
            </div>
            
            <div className="space-y-3 mt-5">
              {masterLevels.advanced.map(skill => (
                <div key={skill.name} className="flex items-center gap-2">
                  <div className={`${skill.color} bg-opacity-90 dark:bg-opacity-80 h-8 w-8 rounded-md flex items-center justify-center text-white`}>
                    {skill.icon}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{skill.name}</span>
                      <span className="text-xs text-zinc-500 dark:text-zinc-400">{skill.level}%</span>
                    </div>
                    <div className="h-1.5 w-full bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
                      <motion.div
                        className={`h-full ${skill.color} rounded-full`}
                        initial={{ width: 0 }}
                        whileInView={{ width: `${skill.level}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: 0.3 }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
        
        {/* Intermediate level */}
        <motion.div 
          className="relative"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          onMouseEnter={() => setActiveCategory('intermediate')}
          onMouseLeave={() => setActiveCategory(null)}
        >
          <div className={`h-full rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6 shadow-sm transition-all duration-300 ${activeCategory === 'intermediate' ? 'shadow-xl ring-2 ring-green-500/30 dark:ring-green-400/20' : 'hover:shadow-md'}`}>
            <div className="mb-4 flex items-center">
              <div className="mr-3 h-10 w-10 rounded-full bg-gradient-to-br from-green-500 to-green-700 dark:from-green-600 dark:to-green-800 text-white flex items-center justify-center">
                <Rocket className="h-5 w-5" />
              </div>
              <h4 className="text-xl font-bold text-zinc-900 dark:text-white">Intermediate</h4>
            </div>
            
            <div className="space-y-3 mt-5">
              {masterLevels.intermediate.map(skill => (
                <div key={skill.name} className="flex items-center gap-2">
                  <div className={`${skill.color} bg-opacity-90 dark:bg-opacity-80 h-8 w-8 rounded-md flex items-center justify-center text-white`}>
                    {skill.icon}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{skill.name}</span>
                      <span className="text-xs text-zinc-500 dark:text-zinc-400">{skill.level}%</span>
                    </div>
                    <div className="h-1.5 w-full bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
                      <motion.div
                        className={`h-full ${skill.color} rounded-full`}
                        initial={{ width: 0 }}
                        whileInView={{ width: `${skill.level}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: 0.3 }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
      
      <div className="text-center mt-10">
        <p className="text-zinc-500 dark:text-zinc-400 text-sm">
          <span className="inline-flex items-center bg-zinc-100 dark:bg-zinc-800 rounded-full px-3 py-1">
            <Brain className="h-3.5 w-3.5 mr-1.5 text-violet-500" />
            <span>Continuously learning and improving these skills</span>
          </span>
        </p>
      </div>
    </div>
  );
};

const ModernEducation = ({ items }) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {items.map((item, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: index * 0.1 }}
          whileHover={{ y: -8, transition: { duration: 0.2 } }}
          className="h-full"
        >
          <Card className="overflow-hidden border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-800/90 h-full shadow hover:shadow-lg transition-all duration-300">
            <div className="relative overflow-hidden h-28">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/90 to-violet-600/90 flex items-center justify-center">
                <div className="w-16 h-16 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
                  {item.icon}
                </div>
                
                {/* Decorative elements */}
                <div className="absolute top-3 right-3">
                  <Calendar className="h-5 w-5 text-white/70" />
                </div>
                <div className="absolute bottom-3 left-3 bg-white/20 backdrop-blur-sm rounded-md px-2 py-1 text-xs text-white font-medium">
                  {item.period}
                </div>
              </div>
            </div>
            
            <CardContent className="p-5">
              <div className="mb-3">
                <h3 className="font-bold text-lg text-zinc-900 dark:text-zinc-100 mb-1">
                  {item.degree}
                </h3>
                <div className="flex items-center text-sm text-zinc-600 dark:text-zinc-400">
                  <Building2 className="h-3.5 w-3.5 mr-1.5" />
                  <span>{item.school}</span>
                </div>
              </div>
              
              <p className="text-zinc-600 dark:text-zinc-400 text-sm leading-relaxed">
                {item.description}
              </p>
            </CardContent>
          </Card>
        </motion.div>
      ))}
    </div>
  );
};


// Interactive personal stats
const PersonalStats = () => {
  const stats = [
    { label: "Projects Completed", value: "30+", icon: <Rocket className="h-6 w-6 text-purple-500" />, color: "from-purple-500/20 to-purple-600/5" },
    { label: "Coffees Consumed", value: "650+", icon: <Coffee className="h-6 w-6 text-amber-500" />, color: "from-amber-500/20 to-amber-600/5" },
    { label: "Lines of Code", value: "250K+", icon: <Code className="h-6 w-6 text-blue-500" />, color: "from-blue-500/20 to-blue-600/5" },
    { label: "Happy Clients", value: "25+", icon: <Smile className="h-6 w-6 text-green-500" />, color: "from-green-500/20 to-green-600/5" },
  ];
  
  return (
    <motion.div 
      initial="hidden"
      whileInView="visible"
      variants={containerVariants}
      viewport={{ once: true }}
      className="grid grid-cols-2 lg:grid-cols-4 gap-4"
    >
      {stats.map((stat, index) => (
        <motion.div
          key={stat.label}
          variants={itemVariants}
          className={`relative rounded-xl overflow-hidden border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-800/90 p-6 text-center group`}
        >
          <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
          <div className="relative z-10">
            <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-zinc-100 dark:bg-zinc-700/70 flex items-center justify-center">
              {stat.icon}
            </div>
            <div className="text-3xl font-bold mb-1 text-zinc-800 dark:text-zinc-100">{stat.value}</div>
            <div className="text-sm text-zinc-500 dark:text-zinc-400">{stat.label}</div>
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
};

// Animated personality traits
const PersonalityTraits = () => {
  const traits = [
    { 
      trait: "Creative", 
      description: "I think outside the box and find innovative solutions.", 
      icon: <Lightbulb className="h-6 w-6" />,
      color: "bg-gradient-to-br from-yellow-400 to-amber-500"
    },
    { 
      trait: "Detail-Oriented", 
      description: "I pay close attention to the small things that make a big difference.", 
      icon: <FileCode2 className="h-6 w-6" />,
      color: "bg-gradient-to-br from-blue-400 to-cyan-500"
    },
    { 
      trait: "Collaborative", 
      description: "I thrive in team environments and love sharing knowledge.", 
      icon: <Users className="h-6 w-6" />,
      color: "bg-gradient-to-br from-green-400 to-emerald-500"
    },
    { 
      trait: "Adaptable", 
      description: "I embrace change and quickly adjust to new technologies and workflows.", 
      icon: <HeartHandshake className="h-6 w-6" />,
      color: "bg-gradient-to-br from-purple-400 to-violet-500"
    },
    { 
      trait: "Curious", 
      description: "I'm always learning and exploring new concepts and technologies.", 
      icon: <Brain className="h-6 w-6" />,
      color: "bg-gradient-to-br from-orange-400 to-red-500"
    },
    { 
      trait: "Persistent", 
      description: "I enjoy tackling challenging problems until they're solved.", 
      icon: <Rocket className="h-6 w-6" />,
      color: "bg-gradient-to-br from-pink-400 to-rose-500"
    },
  ];
  
  const [activeIndex, setActiveIndex] = useState(null);
  
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-6 gap-4 lg:gap-5">
      {traits.map((item, index) => (
        <motion.div 
          key={item.trait}
          className="relative"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          viewport={{ once: true }}
          onHoverStart={() => setActiveIndex(index)}
          onHoverEnd={() => setActiveIndex(null)}
          whileHover={{ y: -5 }}
        >
          <Card className={`h-full cursor-pointer overflow-hidden transition-all duration-300`}>
            <div className={`h-1.5 w-full ${item.color}`}></div>
            <CardContent className="p-4 pt-5 flex flex-col items-center justify-center text-center h-full">
              <div className={`w-12 h-12 rounded-full ${item.color} text-white flex items-center justify-center mb-3`}>
                {item.icon}
              </div>
              
              <div className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 mb-1">
                {item.trait}
              </div>
              
            
            </CardContent>
          </Card>
        </motion.div>
      ))}
    </div>
  );
};

// Main component
const About = () => {
  // Animation for hero section elements
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0]);

  // For interactive constellation background
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: e.clientX / window.innerWidth,
        y: e.clientY / window.innerHeight
      });
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

 return (
    <div className="bg-white dark:bg-zinc-900 overflow-x-hidden">
      {/* About Section */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-b from-zinc-50/80 to-white dark:from-zinc-800/10 dark:to-zinc-900">
        {/* Decorative elements */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-20 -left-10 w-72 h-72 bg-blue-200/10 dark:bg-blue-400/5 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-20 right-10 w-80 h-80 bg-indigo-200/10 dark:bg-indigo-400/5 rounded-full blur-3xl"></div>
          <svg className="absolute right-0 top-0 h-full text-zinc-200 dark:text-zinc-800/20 opacity-10" viewBox="0 0 100 100" preserveAspectRatio="none" fill="currentColor">
            <polygon points="0,0 100,0 100,100" />
          </svg>
        </div>
        
        {/* Content remains the same */}
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 mb-4 px-3 py-1">
              <span className="flex items-center gap-1.5">
                <User className="h-3.5 w-3.5" />
                <span>About Me</span>
              </span>
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-zinc-900 dark:text-white mb-6">
              Who Am I?
            </h2>
            <motion.p 
              className="text-lg text-zinc-700 dark:text-zinc-300 leading-relaxed"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              As a dynamic and creative developer, I thrive at the intersection of technology and design. 
              My passion lies in building intuitive digital experiences that solve real problems. 
              With a strong foundation in both frontend and backend technologies, I bring a holistic 
              approach to every project I tackle.
            </motion.p>
          </div>
          
          {/* Personal Stats */}
          <div className="mb-20">
            <PersonalStats />
          </div>
          
          {/* Personality Section */}
          <motion.div 
            className="mb-24"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.div 
              variants={itemVariants} 
              className="text-center mb-12"
            >
              <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 mb-3 px-3 py-1">
                <span className="flex items-center gap-2">
                  <Sparkles className="h-3.5 w-3.5" />
                  <span>Personality</span>
                </span>
              </Badge>
              <h3 className="text-2xl sm:text-3xl font-bold text-zinc-900 dark:text-white mb-3">
                What Drives Me
              </h3>
              <p className="text-zinc-600 dark:text-zinc-400 max-w-2xl mx-auto">
                These core traits shape how I approach challenges and collaborate with others.
              </p>
            </motion.div>
            
            <PersonalityTraits />
          </motion.div>
        </div>
      </section>
      
      {/* Technical Skills Section with mastery grid */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-b from-zinc-100/70 via-white to-white dark:from-zinc-800/20 dark:via-zinc-900 dark:to-zinc-900">
        {/* Decorative background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -right-20 w-64 h-64 bg-blue-200/10 dark:bg-blue-400/5 rounded-full blur-3xl"></div>
          <div className="absolute top-40 -left-20 w-80 h-80 bg-indigo-200/10 dark:bg-indigo-400/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-full h-1/3 opacity-5">
            <div className="w-full h-full bg-[radial-gradient(circle,_#00000010_1px,_transparent_1px)] bg-[length:20px_20px]"></div>
          </div>
        </div>
        
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            className="relative z-10 text-center mb-12 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300 mb-3 px-3 py-1">
              <span className="flex items-center gap-1.5">
                <Code className="h-3.5 w-3.5" />
                <span>Technical Proficiency</span>
              </span>
            </Badge>
            <h3 className="text-2xl sm:text-4xl font-bold text-zinc-900 dark:text-white mb-4">
              Skills & Expertise
            </h3>
            <p className="text-zinc-600 dark:text-zinc-300 mt-4 text-lg">
              My technical skill set represents years of learning, practice, and professional application.
              Here's my current technical landscape.
            </p>
          </motion.div>
          
          <SkillMasteryGrid skills={hardSkills} />
        </div>
      </section>

      {/* Education with cleaner timeline format */}
      <section className="py-24 relative overflow-hidden bg-white dark:bg-zinc-900">
        {/* Decorative background elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-200/10 dark:bg-blue-400/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 -left-20 w-64 h-64 bg-indigo-200/10 dark:bg-indigo-400/5 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            className="text-center mb-16 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300 mb-3 px-3 py-1">
              <span className="flex items-center gap-1.5">
                <GraduationCap className="h-3.5 w-3.5" />
                <span>Education</span>
              </span>
            </Badge>
            <h2 className="text-2xl sm:text-4xl font-bold text-zinc-900 dark:text-white mb-4">
              Academic Background
            </h2>
            <p className="text-zinc-600 dark:text-zinc-300 text-lg">
              My educational journey has provided me with both theoretical foundations and practical 
              experiences that fuel my professional growth.
            </p>
          </motion.div>

          {/* New Education Timeline */}
          <div className="relative max-w-4xl mx-auto">
            {/* Timeline line */}
            <div className="absolute left-0 md:left-1/2 top-0 h-full w-0.5 bg-blue-100 dark:bg-blue-900/30 transform md:translate-x-[-0.5px]"></div>
            
            {/* Timeline items */}
            <div className="space-y-12">
              {education.map((item, index) => (
                <motion.div 
                  key={item.degree}
                  className="relative"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  {/* Date circle */}
                  <div className="absolute left-[-8px] md:left-1/2 md:transform md:translate-x-[-8px] top-0 z-10">
                    <div className="w-4 h-4 rounded-full bg-blue-500 dark:bg-blue-400 ring-4 ring-white dark:ring-zinc-900"></div>
                  </div>
                  
                  {/* Content */}
                  <div className={`md:w-1/2 ${
                    index % 2 === 0 ? 'md:pr-12 ml-6 md:ml-0' : 'md:pl-12 ml-6 md:ml-auto'
                  }`}>
                    <div className="bg-white dark:bg-zinc-800 rounded-lg shadow-sm border border-zinc-200 dark:border-zinc-700 p-6 hover:shadow-md transition-shadow">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="rounded-full bg-blue-100 dark:bg-blue-900/30 p-2 text-blue-700 dark:text-blue-300">
                          {item.icon}
                        </div>
                        <Badge variant="outline" className="bg-zinc-100/80 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300">
                          {item.period}
                        </Badge>
                      </div>
                      
                      <h3 className="text-xl font-bold text-zinc-900 dark:text-white mb-1">
                        {item.degree}
                      </h3>
                      
                      <div className="flex items-center text-sm text-zinc-600 dark:text-zinc-400 mb-3">
                        <Building2 className="h-3.5 w-3.5 mr-1.5" />
                        <span>{item.school}</span>
                      </div>
                      
                      <p className="text-zinc-600 dark:text-zinc-400 text-sm">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
          
          {/* Education stats */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {[
              { label: "Years of Education", value: "9+", icon: <Calendar className="h-5 w-5 text-blue-500" /> },
              { label: "Certificates", value: "2", icon: <FileCode2 className="h-5 w-5 text-blue-500" /> },
              { label: "Countries", value: "2", icon: <Globe className="h-5 w-5 text-blue-500" /> },
              { label: "Specializations", value: "4", icon: <Star className="h-5 w-5 text-blue-500" /> },
            ].map((stat, i) => (
              <motion.div 
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white/80 dark:bg-zinc-800/80 backdrop-blur-sm rounded-lg border border-zinc-200 dark:border-zinc-700 p-4 text-center flex flex-col items-center"
              >
                <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mb-2">
                  {stat.icon}
                </div>
                <div className="text-2xl font-bold text-zinc-900 dark:text-white">{stat.value}</div>
                <div className="text-xs text-zinc-500 dark:text-zinc-400">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Soft skills section with more cohesive design */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-b from-zinc-100/70 to-white dark:from-zinc-800/20 dark:to-zinc-900">
        {/* Decorative background elements */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-200/10 dark:bg-blue-400/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 -left-20 w-64 h-64 bg-indigo-200/10 dark:bg-indigo-400/5 rounded-full blur-3xl"></div>
          <div className="absolute top-10 left-10 right-10 h-32 opacity-5">
            <div className="w-full h-full bg-[radial-gradient(circle,_#00000010_1px,_transparent_1px)] bg-[length:24px_24px]"></div>
          </div>
        </div>
        
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            className="text-center mb-12 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300 mb-3 px-3 py-1">
              <span className="flex items-center gap-1.5">
                <Brain className="h-3.5 w-3.5" />
                <span>Soft Skills</span>
              </span>
            </Badge>
            <h3 className="text-2xl sm:text-3xl font-bold text-zinc-900 dark:text-white mb-3">
              Beyond Technical Abilities
            </h3>
            <p className="text-zinc-600 dark:text-zinc-400 max-w-2xl mx-auto">
              Technical skills get the job done, but soft skills make the difference in how it's delivered.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-4xl mx-auto">
            {softSkills.map((skill, index) => (
              <motion.div
                key={skill.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="bg-white dark:bg-zinc-800/80 border border-zinc-200 dark:border-zinc-700 rounded-xl shadow-sm hover:shadow-md transition-all p-6">
                  <div className="flex items-center gap-4">
                    <div className="bg-blue-500 text-white h-12 w-12 rounded-lg flex items-center justify-center">
                      {skill.icon}
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg text-zinc-900 dark:text-zinc-100">{skill.name}</h4>
                    </div>
                  </div>
                  <div className="mt-4">
                    <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
                      {skill.name === "Problem Solving" ? "Approaching complex challenges with analytical thinking and creative solutions."
                      : skill.name === "Teamwork" ? "Collaborating effectively and contributing to team success through shared goals."
                      : skill.name === "Creativity" ? "Finding innovative approaches and thinking outside conventional boundaries."
                      : skill.name === "Leadership" ? "Guiding projects and inspiring team members to achieve their best work."
                      : skill.name === "Communication" ? "Clearly conveying ideas and actively listening to stakeholders' needs."
                      : "Quickly adjusting to changing requirements and embracing new methodologies."}
                    </p>
                  </div>
                  <div className="mt-4 pt-3 border-t border-zinc-100 dark:border-zinc-700/50">
                    <div className="h-1.5 w-full bg-zinc-100 dark:bg-zinc-700 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-blue-500 rounded-full"
                        initial={{ width: 0 }}
                        whileInView={{ width: `${85 + index * 2}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: 0.3 }}
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Work process section with cohesive color scheme */}
      <section className="py-24 relative overflow-hidden bg-white dark:bg-zinc-900">
        {/* Decorative elements */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-40 right-10 w-96 h-96 bg-blue-200/10 dark:bg-blue-400/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-20 w-80 h-80 bg-indigo-200/10 dark:bg-indigo-400/5 rounded-full blur-3xl"></div>
          <div className="absolute top-1/3 left-1/4 opacity-10 dark:opacity-5">
            <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M30 60C30 43.4315 43.4315 30 60 30V60H30Z" fill="#3B82F6"/>
              <path d="M60 30C76.5685 30 90 43.4315 90 60H60V30Z" fill="#3B82F6"/>
              <path d="M90 60C90 76.5685 76.5685 90 60 90V60H90Z" fill="#3B82F6"/>
              <path d="M60 90C43.4315 90 30 76.5685 30 60H60V90Z" fill="#3B82F6"/>
            </svg>
          </div>
        </div>
        
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            className="text-center mb-16 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300 mb-3 px-3 py-1">
              <span className="flex items-center gap-1.5">
                <Rocket className="h-3.5 w-3.5" />
                <span>Work Process</span>
              </span>
            </Badge>
            <h2 className="text-2xl sm:text-4xl font-bold text-zinc-900 dark:text-white mb-4">
              How I Work
            </h2>
            <p className="text-zinc-600 dark:text-zinc-300 text-lg">
              My approach combines structure with flexibility, ensuring consistent quality while adapting to each project's unique requirements.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              { title: "Discover", description: "Understanding your requirements and objectives in depth.", icon: <Lightbulb className="h-6 w-6" /> },
              { title: "Design", description: "Creating structured solutions tailored to your specific needs.", icon: <PenTool className="h-6 w-6" /> },
              { title: "Develop", description: "Building with quality code and attention to performance.", icon: <Code className="h-6 w-6" /> },
              { title: "Deploy", description: "Launching and maintaining with reliable support.", icon: <Rocket className="h-6 w-6" /> },
            ].map((step, i) => (
              <motion.div 
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative"
              >
                <Card className="overflow-hidden h-full border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-800 shadow-sm hover:shadow-md transition-all duration-300">
                  <div className="h-2 w-full bg-blue-500 dark:bg-blue-600"></div>
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="mb-4 flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white bg-blue-500 dark:bg-blue-600">
                        {step.icon}
                      </div>
                      <span className="text-zinc-500 dark:text-zinc-400 font-semibold text-sm">
                        Step {i + 1}
                      </span>
                    </div>
                    
                    <h3 className="text-xl font-bold text-zinc-900 dark:text-white mb-3">{step.title}</h3>
                    
                    <p className="text-zinc-600 dark:text-zinc-400 text-sm flex-grow">
                      {step.description}
                    </p>
                    
                    {i < 3 && (
                      <div className="hidden md:block absolute -right-3 top-1/2 transform -translate-y-1/2">
                        <ChevronRight className="h-6 w-6 text-blue-500 dark:text-blue-400" />
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section with consistent blue theme */}
      <section className="py-20 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="max-w-3xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-zinc-900 dark:text-white mb-6">
              Let's Create Something <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Amazing</span> Together
            </h2>
            <p className="text-lg text-zinc-700 dark:text-zinc-300 mb-8">
              I'm always open to discussing new projects, creative ideas, or opportunities to be part of your vision.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg shadow-lg transition-all">
                <Link to="/contact" className="flex items-center gap-2">
                  Get In Touch
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              
              <Button 
                asChild 
                variant="outline" 
                size="lg" 
                className="rounded-lg border-2 border-zinc-200 dark:border-zinc-700 bg-white/80 dark:bg-zinc-800/80 backdrop-blur-sm hover:bg-white dark:hover:bg-zinc-800 transition-all text-zinc-900 dark:text-zinc-100"
              >
                <Link to="/projects" className="flex items-center gap-2">
                  Explore Projects
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
export default About;